'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  alpha,
  Group,
  Indicator,
  Loader,
  Popover,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBell,
  IconCheck,
  IconChecks,
  IconCircleCheck,
  IconClock,
  IconDownload,
  IconFileText,
  IconMail,
  IconReceipt2,
  IconRefresh,
} from '@tabler/icons-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import useEmailResponseNotifications, {
  parseResponseTime,
  type EmailResponseAttachment,
  type EmailResponseNotificationItem,
} from '@hooks/notifications/use-email-response-notifications';

const ICON_SIZE = 20;

/**
 * The bell's type scale.
 *
 * Every size lives here rather than being spelled as a Mantine `size` on one
 * line and a raw `fontSize` on the next, which is how the list ended up mixing
 * 14px body text with 11px controls. The scale is one step below the app's
 * default reading sizes throughout: this is a dense scanning surface in a
 * 380px popover, not a page.
 *
 * `family` restates the app font rather than relying on inheritance so the
 * dropdown — which renders in a portal, outside the dashboard subtree — is
 * never at the mercy of whatever it is portalled next to.
 */
const TYPE = {
  family: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  /** The row's headline: company plus what it received. */
  lead: 12.5,
  /** Supporting text: AJU numbers, empty states, control labels. */
  body: 11,
  /** Timestamps and the smallest labels. */
  meta: 10,
  /** Download buttons and the urgent badge. */
  pill: 10,
  /** The one number the header exists to show. */
  timer: 13,
} as const;

/**
 * Reference numbers and clock readings are compared down a column, so they get
 * fixed-width digits. Without this, Inter's proportional numerals make two AJUs
 * of the same length render at different widths.
 */
const NUMERIC = { fontVariantNumeric: 'tabular-nums' } as const;

/**
 * CEISA AJU numbers are stored unpunctuated but always read as
 * 000020-029326-20251017-005630. Anything that is not that length is shown
 * untouched rather than mangled into a wrong grouping.
 */
const formatNomorAju = (nomorAju: string): string => {
  if (!nomorAju) return '';
  const clean = nomorAju.replace(/-/g, '');
  if (clean.length < 26) return nomorAju;
  return `${clean.slice(0, 6)}-${clean.slice(6, 12)}-${clean.slice(12, 20)}-${clean.slice(20, 26)}`;
};

/**
 * The last six characters of an AJU, which is how a shipment is referred to in
 * conversation -- "803207" rather than the full 000020MIJ42320260717803207.
 * The same short form the response email prints as its REF.
 */
const formatAjuShortRef = (nomorAju: string): string => {
  const clean = (nomorAju || '').replace(/-/g, '');
  return clean.length >= 6 ? clean.slice(-6) : clean;
};

/**
 * The response's own timestamp, as "27 Agu 2026, 14:32". This is the reading
 * the row leads with -- an exact time, not an age, because acting on a response
 * means knowing when it actually landed.
 */
const formatResponDateTime = (item: EmailResponseNotificationItem): string => {
  const at = parseResponseTime(item);
  if (at === null) return item.waktuRespon || '';

  const parsed = new Date(at);
  const date = parsed.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = parsed.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}, ${time}`;
};

const formatWaktuRespon = (waktuRespon: string): string => {
  if (!waktuRespon) return '';
  const parsed = new Date(waktuRespon);
  if (Number.isNaN(parsed.getTime())) return waktuRespon;

  return parsed.toLocaleString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * How long ago the newest response landed, as hh:mm.
 *
 * Zero-padded and never abbreviated, because the point of this number is that
 * it is read the same way every time it is glanced at. Hours are not wrapped
 * at 24: "38:20" is the honest reading for a queue nobody has looked at since
 * yesterday, and "14:20" would not be.
 */
const formatElapsed = (since: Date | null): string | null => {
  if (!since) return null;

  const totalMinutes = Math.floor((Date.now() - since.getTime()) / 60000);
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return null;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** The timer only shows minutes, so it only needs to move once a minute. */
const ELAPSED_TICK_MS = 30 * 1000;

interface ResponseTone {
  color: string;
  Icon: typeof IconMail;
}

/**
 * Tone follows the response's meaning, matching the popup notifications
 * already used elsewhere so the two surfaces agree about what SPPB or NPD
 * looks like. Colour only reaches the small leading glyph -- the row text
 * itself stays neutral so a list of ten responses does not read as ten alerts.
 *
 * Urgency is decided by the server (`isUrgent`) rather than re-derived here,
 * so the badge and this glyph cannot disagree about the same response.
 */
const responseTone = (
  notification: EmailResponseNotificationItem,
  themeColors: Record<string, string>
): ResponseTone => {
  if (notification.isUrgent) return { color: themeColors.red, Icon: IconAlertTriangle };

  const upper = (notification.uraianSingkatRespon || '').toUpperCase();
  if (upper.includes('SPJM')) return { color: themeColors.red, Icon: IconAlertTriangle };
  if (upper.includes('SPPB') || upper.includes('NPE')) {
    return { color: themeColors.green, Icon: IconCircleCheck };
  }
  if (upper.includes('BILLING')) return { color: themeColors.warning, Icon: IconReceipt2 };
  return { color: themeColors.accent, Icon: IconMail };
};

interface AttachmentButtonProps {
  attachment: EmailResponseAttachment;
  onClick: () => Promise<boolean>;
  t: (key: string) => string;
}

/** Where a button is in its download: idle, in flight, finished, or failed. */
type DownloadState = 'idle' | 'loading' | 'done' | 'error';

/** How long the finished/failed state stays on the button before it resets. */
const DOWNLOAD_FEEDBACK_MS = { done: 2000, error: 2800 } as const;

/**
 * One download button.
 *
 * A notification carries the declaration draft plus, usually, the response
 * document -- so at most two of these, and never fewer than one. The two are
 * given different weights: the response is what the user came for, the draft is
 * the thing they will want next, so it sits alongside in a quieter treatment
 * rather than competing.
 *
 * The button also reports its own download rather than delegating that
 * entirely to the toast: a browser that saves a PDF straight to the downloads
 * folder gives no visible sign at the click, and the toast is in the opposite
 * corner from the pill the user is still looking at. So the icon runs through
 * spinner -> check (or a warning triangle when the fetch failed) in place, and
 * the pill tints to match for a couple of seconds before returning to normal.
 */
function AttachmentButton({ attachment, onClick, t }: AttachmentButtonProps) {
  const { colors: themeColors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [state, setState] = useState<DownloadState>('idle');
  const resetTimer = useRef<number | null>(null);
  const mounted = useRef(true);

  // The reset is on a timer, and the dropdown can close (unmounting the row)
  // long before it fires.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    };
  }, []);

  const isDeclaration = attachment.kind === 'dokumenPabean';

  const handleClick = async () => {
    // A second click while the first request is still open would download the
    // same file twice and restart the feedback halfway through.
    if (state === 'loading') return;
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);

    setState('loading');
    const ok = await onClick();
    if (!mounted.current) return;

    const outcome: DownloadState = ok ? 'done' : 'error';
    setState(outcome);
    resetTimer.current = window.setTimeout(() => {
      if (mounted.current) setState('idle');
    }, DOWNLOAD_FEEDBACK_MS[outcome]);
  };

  // Finished and failed both speak for themselves and outrank the hover tint.
  const accentColor = state === 'done' ? themeColors.green : state === 'error' ? themeColors.error : themeColors.accent;
  const isSignalling = state === 'done' || state === 'error';

  const borderColor = isSignalling ? accentColor : hovered ? themeColors.accent : themeColors.border;
  const textColor = isSignalling
    ? accentColor
    : hovered || !isDeclaration
      ? themeColors.text
      : themeColors.textSecondary;

  const tooltipLabel =
    state === 'loading'
      ? `${t('notifications.bell.downloading')} ${attachment.label}`
      : state === 'done'
        ? `${attachment.label} ${t('notifications.bell.downloaded')}`
        : state === 'error'
          ? `${t('notifications.bell.downloadFailed')} ${attachment.label}`
          : `${t('notifications.bell.download')} ${attachment.label}`;

  return (
    <Tooltip label={tooltipLabel} withinPortal openDelay={isSignalling ? 0 : 400} opened={isSignalling || undefined}>
      <UnstyledButton
        onClick={handleClick}
        aria-busy={state === 'loading'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 7px',
          borderRadius: 999,
          border: `1px solid ${borderColor}`,
          backgroundColor: isSignalling
            ? alpha(accentColor, 0.14)
            : hovered
              ? themeColors.dropdownHover
              : 'transparent',
          boxShadow: hovered && !isSignalling ? `0 0 0 2px ${alpha(themeColors.accent, 0.14)}` : 'none',
          fontFamily: TYPE.family,
          fontSize: TYPE.pill,
          fontWeight: isDeclaration ? 500 : 600,
          letterSpacing: 0.2,
          lineHeight: 1.7,
          color: textColor,
          cursor: state === 'loading' ? 'progress' : 'pointer',
          transform: hovered && state !== 'loading' ? 'translateY(-1px)' : 'none',
          transition:
            'background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <AttachmentIcon state={state} isDeclaration={isDeclaration} color={accentColor} />
        {attachment.label}
      </UnstyledButton>
    </Tooltip>
  );
}

interface AttachmentIconProps {
  state: DownloadState;
  isDeclaration: boolean;
  color: string;
}

/**
 * The 11px glyph on a download pill.
 *
 * All four states share one slot so the pill never changes width mid-download
 * and shift the row beside it.
 */
function AttachmentIcon({ state, isDeclaration, color }: AttachmentIconProps) {
  if (state === 'loading') return <Loader size={11} color={color} />;
  if (state === 'done') return <IconCheck size={11} strokeWidth={2.4} />;
  if (state === 'error') return <IconAlertTriangle size={11} strokeWidth={2.2} />;

  const Icon = isDeclaration ? IconFileText : IconDownload;
  return <Icon size={11} strokeWidth={1.9} />;
}

interface NotificationItemProps {
  notification: EmailResponseNotificationItem;
  onOpen: (notification: EmailResponseNotificationItem) => void;
  onDownload: (notification: EmailResponseNotificationItem, attachmentIndex: number) => Promise<boolean>;
  t: (key: string) => string;
}

function NotificationItem({ notification, onOpen, onDownload, t }: NotificationItemProps) {
  const { colors: themeColors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const { color, Icon } = responseTone(notification, themeColors as Record<string, string>);
  const isUnread = !notification.isRead;
  const respon = (notification.uraianSingkatRespon || notification.kodeRespon || 'RESPON').toUpperCase();
  const shortRef = formatAjuShortRef(notification.nomorAju);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        // Hover is the only row chrome: no rails, tints or dividers, so the
        // list reads as one column of text with an icon.
        backgroundColor: hovered ? themeColors.dropdownHover : 'transparent',
        borderRadius: 10,
        padding: '9px 10px 9px 8px',
        transition: 'background-color 120ms ease',
      }}
    >
      <UnstyledButton
        onClick={() => onOpen(notification)}
        style={{ display: 'block', width: '100%', textAlign: 'left' }}
      >
        <Group gap={9} wrap="nowrap" align="flex-start">
          <div
            style={{
              flexShrink: 0,
              width: 26,
              height: 26,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${color}1F`,
              color,
              marginTop: 1,
            }}
          >
            <Icon size={13} strokeWidth={1.8} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Group gap={8} wrap="nowrap" align="flex-start" justify="space-between">
              {/* Response type, then whose shipment it is, then which one.
                  The type leads because it is what the row is about; a list
                  scanned for "is my SPPB in yet" is scanned down this column. */}
              <Text
                lh={1.45}
                lineClamp={2}
                style={{ fontFamily: TYPE.family, fontSize: TYPE.lead, color: themeColors.text, flex: 1 }}
              >
                <span style={{ fontWeight: 600 }}>{respon}</span>
                <span style={{ color: themeColors.textSecondary }}> - </span>
                <span style={{ fontWeight: 600 }}>{notification.namaPerusahaan || '-'}</span>
                {shortRef && <span style={{ color: themeColors.textSecondary, ...NUMERIC }}> ({shortRef})</span>}
                {notification.isUrgent && <UrgentBadge t={t} />}
              </Text>

              {/* Unread is a single dot rather than a badge plus tint plus rail. */}
              <div
                style={{
                  flexShrink: 0,
                  marginTop: 5,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: isUnread ? themeColors.accent : 'transparent',
                }}
              />
            </Group>

            {/* The exact time the response landed. The full AJU is one hover
                away rather than spending a line of its own on 26 digits. */}
            <Tooltip label={formatNomorAju(notification.nomorAju) || '-'} withinPortal openDelay={300}>
              <Text
                mt={2}
                lineClamp={1}
                style={{
                  fontFamily: TYPE.family,
                  fontSize: TYPE.body,
                  color: themeColors.textSecondary,
                  ...NUMERIC,
                }}
              >
                {formatResponDateTime(notification)}
              </Text>
            </Tooltip>
          </div>
        </Group>
      </UnstyledButton>

      {notification.attachments.length > 0 && (
        <Group gap={5} mt={6} wrap="wrap" style={{ paddingLeft: 35 }}>
          {notification.attachments.map((attachment) => (
            <AttachmentButton
              key={attachment.index}
              attachment={attachment}
              onClick={() => onDownload(notification, attachment.index)}
              t={t}
            />
          ))}
        </Group>
      )}
    </div>
  );
}

/**
 * Marks the responses that cost the importer money or time if they sit unread:
 * NPD asks for a document the declaration is missing, and SPTNP is an
 * underpayment assessment with a payment deadline on it. Rendered inline in the
 * headline so it travels with the two-line clamp instead of being pushed out of
 * view by a long company name.
 */
function UrgentBadge({ t }: { t: (key: string) => string }) {
  const { colors: themeColors } = useTheme();

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        marginLeft: 6,
        padding: '0 5px',
        borderRadius: 4,
        backgroundColor: `${themeColors.red}1F`,
        color: themeColors.red,
        fontFamily: TYPE.family,
        fontSize: TYPE.pill,
        fontWeight: 700,
        letterSpacing: 0.4,
        lineHeight: 1.6,
        textTransform: 'uppercase',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      }}
    >
      <IconAlertTriangle size={9} strokeWidth={2.4} />
      {t('notifications.bell.urgent')}
    </span>
  );
}

/**
 * The dashboard header bell: a red badge while there are unread CEISA email
 * responses, and a dropdown listing the most recent ones with a download
 * button per attached document.
 */
export default function NotificationBell() {
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const [opened, setOpened] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const {
    items,
    unreadCount,
    hasUnread,
    lastResponseAt,
    isLoading,
    isError,
    refetch,
    markRead,
    markAllRead,
    isMarkingAllRead,
    downloadAttachment,
  } = useEmailResponseNotifications();

  const badgeLabel = useMemo(() => (unreadCount > 99 ? '99+' : String(unreadCount)), [unreadCount]);

  const visibleItems = useMemo(
    () => (filter === 'unread' ? items.filter((item) => !item.isRead) : items),
    [items, filter]
  );

  // The elapsed reading is state rather than a derived value: what makes it
  // change is the clock, which nothing re-renders on. It is recomputed when the
  // dropdown opens, when a newer response arrives, and on a tick in between.
  //
  // The interval only runs while the dropdown is open. Nothing about the closed
  // bell shows a clock reading, and leaving it running would wake the tab once
  // a minute for a number no one is looking at.
  const [elapsed, setElapsed] = useState<string | null>(null);
  useEffect(() => {
    if (!opened) return;

    const update = () => setElapsed(formatElapsed(lastResponseAt));
    update();

    const interval = window.setInterval(update, ELAPSED_TICK_MS);
    return () => window.clearInterval(interval);
  }, [opened, lastResponseAt]);

  const handleToggle = () => setOpened((previous) => !previous);

  // Opening an item is the read signal; the request is fire-and-forget because
  // the cache is updated optimistically and a failed mark simply leaves the
  // item unread on the next refetch.
  const handleOpenItem = (notification: EmailResponseNotificationItem) => {
    if (!notification.isRead) markRead(notification.id);
  };

  // Resolves to whether the PDF reached the user, which is what the pill that
  // started the download shows in place.
  const handleDownload = async (notification: EmailResponseNotificationItem, attachmentIndex: number) => {
    const attachment = notification.attachments.find((candidate) => candidate.index === attachmentIndex);
    if (!attachment) return false;

    // Downloading a response is also reading it.
    if (!notification.isRead) markRead(notification.id);
    return downloadAttachment(notification.id, attachment);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={380}
      position="bottom-end"
      shadow="lg"
      radius="lg"
      withinPortal
      // SystemNotificationBanner is pinned at z-10000 and would otherwise be
      // painted over the top of this dropdown, hiding its header and the
      // "mark all as read" button whenever a system notice is showing.
      zIndex={10001}
    >
      <Popover.Target>
        <Indicator
          color="red"
          size={16}
          // Pulls the badge down/inward so it overlaps the bell's shoulder
          // rather than floating above the icon.
          offset={7}
          label={hasUnread ? badgeLabel : undefined}
          disabled={!hasUnread}
          styles={{
            indicator: {
              fontSize: 10,
              fontWeight: 700,
              padding: '0 4px',
              // The badge centres its label as a flex item, so what gets
              // centred is the line box, not the digit. Inheriting the app's
              // 1.55 line height makes that box tall enough to park the
              // numeral ~0.7px above the circle's middle -- small, but plainly
              // visible at 16px. 1.1 shrinks the box back around the digit.
              lineHeight: 1.1,
            },
          }}
        >
          <Tooltip label={t('notifications.bell.title')}>
            <ActionIcon
              variant="subtle"
              size="lg"
              radius="xl"
              aria-label={t('notifications.bell.aria')}
              onClick={handleToggle}
              style={{ color: themeColors.text }}
            >
              <IconBell size={ICON_SIZE} strokeWidth={1.6} />
            </ActionIcon>
          </Tooltip>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown
        p={0}
        style={{
          backgroundColor: themeColors.card,
          borderColor: themeColors.border,
          fontFamily: TYPE.family,
          overflow: 'hidden',
          // Keeps a wheel/trackpad gesture started anywhere in the dropdown --
          // including the header and empty/loading states -- from bubbling out
          // and scrolling the dashboard behind it.
          overscrollBehavior: 'contain',
        }}
      >
        {/* Header carries how long the queue has been sitting, the one bulk
            action, and the filter. Still no title and no divider under it. */}
        <Stack gap={6} px={10} pt={9} pb={6}>
          <Group justify="space-between" wrap="nowrap" gap={8}>
            {elapsed ? (
              <Tooltip
                label={`${t('notifications.bell.lastResponseAt')} ${formatWaktuRespon(lastResponseAt?.toISOString() ?? '')}`}
                withinPortal
              >
                <Group gap={5} wrap="nowrap" style={{ minWidth: 0, color: themeColors.textSecondary }}>
                  <IconClock size={12} strokeWidth={1.8} style={{ flexShrink: 0 }} />
                  <Text
                    style={{
                      fontFamily: TYPE.family,
                      fontSize: TYPE.timer,
                      fontWeight: 600,
                      color: themeColors.text,
                      ...NUMERIC,
                    }}
                  >
                    {elapsed}
                  </Text>
                  <Text
                    truncate
                    style={{ fontFamily: TYPE.family, fontSize: TYPE.meta, color: themeColors.textSecondary }}
                  >
                    {t('notifications.bell.sinceLastResponse')}
                  </Text>
                </Group>
              </Tooltip>
            ) : (
              <span />
            )}

            <UnstyledButton
              onClick={() => markAllRead()}
              disabled={!hasUnread || isMarkingAllRead}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontFamily: TYPE.family,
                fontSize: TYPE.body,
                color: themeColors.textSecondary,
                opacity: hasUnread ? 1 : 0.45,
                cursor: hasUnread ? 'pointer' : 'default',
                flexShrink: 0,
              }}
            >
              {isMarkingAllRead ? <Loader size={11} /> : <IconChecks size={13} strokeWidth={1.8} />}
              {t('notifications.bell.markAllRead')}
            </UnstyledButton>
          </Group>

          <SegmentedControl
            size="xs"
            radius="md"
            value={filter}
            onChange={(value) => setFilter(value as 'all' | 'unread')}
            data={[
              { value: 'all', label: t('notifications.bell.filterAll') },
              {
                value: 'unread',
                label: hasUnread
                  ? `${t('notifications.bell.filterUnread')} ${unreadCount}`
                  : t('notifications.bell.filterUnread'),
              },
            ]}
            styles={{
              root: { backgroundColor: 'transparent', padding: 0 },
              indicator: { backgroundColor: themeColors.dropdownHover, boxShadow: 'none' },
              label: {
                fontFamily: TYPE.family,
                fontSize: TYPE.body,
                color: themeColors.textSecondary,
                fontWeight: 500,
                padding: '2px 11px',
              },
            }}
          />
        </Stack>

        {isLoading && (
          <Group justify="center" py={26} gap="xs">
            <Loader size="xs" />
            <Text style={{ fontFamily: TYPE.family, fontSize: TYPE.body, color: themeColors.textSecondary }}>
              {t('notifications.bell.loading')}
            </Text>
          </Group>
        )}

        {!isLoading && isError && (
          <Stack align="center" gap={7} py={26}>
            <Text style={{ fontFamily: TYPE.family, fontSize: TYPE.body, color: themeColors.textSecondary }}>
              {t('notifications.bell.error')}
            </Text>
            <UnstyledButton
              onClick={() => refetch()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontFamily: TYPE.family,
                fontSize: TYPE.body,
                color: themeColors.accent,
              }}
            >
              <IconRefresh size={12} strokeWidth={1.8} />
              {t('notifications.bell.retry')}
            </UnstyledButton>
          </Stack>
        )}

        {!isLoading && !isError && visibleItems.length === 0 && (
          <Stack align="center" gap={3} py={26} px="md">
            <Text fw={600} style={{ fontFamily: TYPE.family, fontSize: TYPE.body, color: themeColors.text }}>
              {filter === 'unread' ? t('notifications.bell.emptyUnread') : t('notifications.bell.empty')}
            </Text>
            <Text
              ta="center"
              style={{ fontFamily: TYPE.family, fontSize: TYPE.meta, color: themeColors.textSecondary }}
            >
              {filter === 'unread' ? t('notifications.bell.emptyUnreadHint') : t('notifications.bell.emptyHint')}
            </Text>
          </Stack>
        )}

        {!isLoading && !isError && visibleItems.length > 0 && (
          <ScrollArea.Autosize
            mah={420}
            type="auto"
            // Without this the list hands its leftover scroll to the page once
            // it hits the top or bottom, so the dashboard moves underneath the
            // open dropdown.
            styles={{ viewport: { overscrollBehavior: 'contain' } }}
          >
            <Stack gap={2} px={6} pb={8}>
              {visibleItems.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onOpen={handleOpenItem}
                  onDownload={handleDownload}
                  t={t}
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
